import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home, ChevronDown } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const DEFAULT_NRI_NOTICE_TEXT = 'Eligibility for an NRI home loan can depend on factors such as income, employment, repayment capacity and documentation. Read our detailed guide to <a href="https://ahamhfc.com/home-loan-blog-aham-housing-finance/40"><strong><u>NRI home loan eligibility in India</u></strong></a> to understand who can apply.';

const NRILoan = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Own a Home in India with Aham NRI Home Loan',
    subtitle: 'Specially designed for salaried NRIs—simple documentation, flexible tenure, and secure funding for your Indian property.',
    image_url: 'img/loan-banners/NRI-Loan-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Fallback static overview data
  const fallbackOverview = {
    title: 'Home Loans for NRI Salaried',
    description: `As a Non-Resident Indian, investing in a home back in India is more than a financial decision — it's a personal milestone. Aham Housing Finance offers dedicated Home Loans for Salaried NRIs designed specifically to help you buy ready-to-move or under-construction residential properties from approved developers.`,
    notice_text: DEFAULT_NRI_NOTICE_TEXT,
    features: [
      'Loan amounts from ₹10 lakhs to ₹50 lakhs – Designed for NRI salaried profiles and approved properties.',
      'Repayment tenure up to 15 years – Choose a tenure that fits your long-term financial goals.',
      'Repayment through NRE/NRO accounts – Hassle-free EMI payments from overseas income.',
      'Exclusive for Salaried NRIs – Ensures stable income verification and streamlined approval.'
    ],
    image_url: 'img/loan-overview/NRI-Overview.webp'
  };

  // Key Benefits state
  const [dynamicBenefits, setDynamicBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState({
    section_title: 'Key Benefits & Features',
    section_subtitle: 'Discover why Aham NRI Home Loan is the right choice for building your dream home'
  });
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  // Icon mapping for dynamic benefits
  const iconMap = {
    HomeIcon,
    BanknotesIcon,
    ClockIcon,
    ShieldCheckIcon,
    CurrencyRupeeIcon,
    ChartBarIcon,
    DocumentTextIcon
  };

  // Fallback static Key Benefits Data
  const benefits = [
    {
      icon: BanknotesIcon,
      title: "Loan up to ₹50 Lakhs",
      description: "Get financing between ₹10 lakhs and ₹50 lakhs for ready or under-construction homes."
    },
    {
      icon: ClockIcon,
      title: "Tenure up to 15 Years",
      description: "Repay comfortably with flexible loan tenures designed for NRI salaried professionals."
    },
    {
      icon: ChartBarIcon,
      title: "Competitive & Transparent Rates",
      description: "Enjoy attractive interest rates with clear, simple repayment options."
    },
    {
      icon: ShieldCheckIcon,
      title: "Exclusively for Salaried NRIs",
      description: "Loans offered only to salaried NRIs for added security and income stability."
    },
    {
      icon: CurrencyRupeeIcon,
      title: "Easy Repayment Channels",
      description: "Repay via normal banking channels or through NRE/NRO accounts with ease."
    },
    {
      icon: HomeIcon,
      title: "Approved Projects Only",
      description: "Financing available for ready or under-construction properties from approved builders."
    }
  ];

  // Eligibility Criteria state
  const [dynamicEligibility, setDynamicEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState({
    section_title: 'Eligibility Criteria',
    section_subtitle: "Find out if you're eligible for Aham NRI Home Loan"
  });
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Icon mapping for dynamic eligibility
  const eligibilityIconMap = {
    User,
    Globe,
    Briefcase,
    DollarSign,
    FileText,
    Shield,
    Star,
    Ruler
  };

  // Fallback static Eligibility Criteria
  const eligibility = [
    "Applicant must hold a valid Indian passport and NRI status",
    "Minimum 1 year of continuous overseas employment",
    "Minimum monthly net salary of ₹80,000 credited in bank",
    "Age between 21 and 60 years (or retirement age, whichever is earlier)",
    "Mandatory Indian resident co-applicant who must be a blood relative",
    "Country of employment must not be in Aham's restricted/negative list",
    "Good credit history and CIBIL compliance required",
    'Stable income source',
  ];

  // Matching icons & colors for each item
  const icons = [
    { icon: <User />, from: "from-blue-500", to: "to-cyan-400" },
    { icon: <Globe />, from: "from-green-500", to: "to-lime-400" },
    { icon: <Briefcase />, from: "from-pink-500", to: "to-rose-400" },
    { icon: <DollarSign />, from: "from-orange-500", to: "to-amber-400" },
    { icon: <FileText />, from: "from-purple-500", to: "to-indigo-400" },
    { icon: <Shield />, from: "from-teal-500", to: "to-emerald-400" },
    { icon: <Star />, from: "from-yellow-500", to: "to-orange-400" },
    { icon: <Ruler />, from: "from-red-500", to: "to-orange-400" }
  ];

  // Documents Required state
  const [dynamicDocuments, setDynamicDocuments] = useState([]);
  const [documentsSettings, setDocumentsSettings] = useState({
    section_title: 'Documents Required',
    section_subtitle: 'Keep these documents ready for a smooth application process'
  });
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Interest Rates state
  const [dynamicInterestRates, setDynamicInterestRates] = useState([]);
  const [interestRatesSettings, setInterestRatesSettings] = useState({
    section_title: 'Affordable Home Loan',
    section_subtitle: 'Interest Rates',
    intro_text: 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make NRI home loans affordable and stress-free.',
    closing_text: 'Begin your NRI home loan journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.',
    cta_text: 'Calculate EMI',
    cta_link: 'https://aham.mbwhost.in/emi-calculator'
  });
  const [interestRatesLoading, setInterestRatesLoading] = useState(true);

  // FAQs state
  const [dynamicFAQs, setDynamicFAQs] = useState([]);
  const [faqsSettings, setFaqsSettings] = useState({
    section_title: 'Frequently Asked Questions',
    section_subtitle: 'Find answers to common questions about Home NRI Loan'
  });
  const [faqsLoading, setFaqsLoading] = useState(true);

  // CTA Banner state
  const [ctaBannerSettings, setCtaBannerSettings] = useState({
    title: 'Ready to Start Your NRI Home Loan Journey?',
    description: 'Take the first step towards owning your dream home in India with Aham\'s hassle-free NRI loan process',
    button_text: 'Talk to Our Loan Expert',
    button_link: 'https://aham.mbwhost.in/contact'
  });
  const [ctaBannerLoading, setCtaBannerLoading] = useState(true);

  // Fallback static Documents Required
  const documents = {
    common: [
      "Completed loan application form with passport-size photographs",
      "Identity Proof – Aadhaar, PAN, Passport, Driving License, Voter ID",
      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement, etc.",
      "Date of Birth Proof – Passport, PAN, Driving License, Birth Certificate"
    ],

    salaried: [
      "Salary slips for the last 3 months",
      "Bank statements showing salary credits for the last 6 months",
      "Form 16 or Income Tax Return (ITR) copies",
      'Employee ID Card'
    ],

    selfEmployed: [
      "Audited financial statements / ITRs for the last 3 years",
      "Bank statements (last 12 months)",
      "Business registration documents – GST, TIN, Trade License, etc.",
      "Business License / Trade License"
    ],

    nri: [
      "Valid Indian passport and overseas visa",
      "Employment contract and working proof",
      "NRE/NRO bank statements for last 6 months",
      "Power of Attorney (if applicable)",
      "Salary slips / income proof as per country of employment",
      "OCI/PIO Card (if applicable)"
    ],

    property: [
      "Registered Sale Deed / Title documents",
      "Approved building plan and property valuation report",
      "Legal and technical appraisal reports",
      "Latest property tax receipts"
    ]
  };


   // Ilaiyaboopathi R
  // FAQs
  const faqs = [
    {
      question: "Can I avail a loan for constructing a house on my own plot?",
      answer:
        "Yes, Aham offers Home Construction Loans with flexible repayment options of up to 20 years for customers building on self-owned land."
    },
    {
      question: "What is a composite loan and who should opt for it?",
      answer:
        "A composite loan finances both land purchase and subsequent home construction. It is ideal for customers who want a single loan instead of taking separate loans for the plot and construction."
    },
    {
      question: "Are NRIs eligible for home loans?",
      answer:
        "Yes, salaried NRIs working in approved countries can apply for home loans with Aham, subject to eligibility criteria and document verification."
    },
    {
      question: "What is the maximum loan amount available?",
      answer:
        "Eligible NRI salaried applicants can avail up to ₹50 lakhs for ready or under-construction homes."
    },
    {
      question: "Is there any prepayment or foreclosure penalty?",
      answer:
        "Prepayment and foreclosure charges may apply as per Aham’s policy. Loans can be closed fully or partially with applicable charges."
    },
    {
      question: "How is the loan amount determined?",
      answer:
        "Loan eligibility depends on income stability, repayment capacity, credit score, property valuation, and LTV guidelines."
    },
    {
      question: "What are the age limits for applying?",
      answer:
        "Minimum age at loan application is 21 years. Maximum age at loan maturity is 60 years or the applicant’s retirement age."
    }
  ];



  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Fetch banner data on component mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/nri_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          // Use first active banner for this page
          setBanner(banners[0]);
        } else {
          // Use fallback if no banner found
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        // Use fallback on error
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch overview data on component mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get('/api/admin/loan-overview/nri_loan');
        const data = response.data.data || null;

        if (data) {
          // Parse features safely
          let features = [];
          if (data.features) {
            try {
              features = typeof data.features === 'string' ? JSON.parse(data.features) : data.features;
              if (!Array.isArray(features)) features = [];
            } catch (e) {
              console.error('Error parsing features:', e);
              features = [];
            }
          }

          const parsedData = {
            ...data,
            features
          };
          setOverview(parsedData);
        } else {
          // Use fallback if no overview found
          setOverview(fallbackOverview);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        // Use fallback on error
        setOverview(fallbackOverview);
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, []);

  // Fetch dynamic benefits and settings
  useEffect(() => {
    const fetchBenefitsData = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/key-benefits/nri_loan/active`),
          axios.get(`/api/admin/key-benefits/settings/nri_loan`)
        ]);

        if (benefitsRes.data.success) {
          setDynamicBenefits(benefitsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits data:', error);
      } finally {
        setBenefitsLoading(false);
      }
    };

    fetchBenefitsData();
  }, []);

  // Fetch dynamic eligibility criteria and settings
  useEffect(() => {
    const fetchEligibilityData = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/eligibility/nri_loan/active`),
          axios.get(`/api/admin/eligibility/settings/nri_loan`)
        ]);

        if (criteriaRes.data.success) {
          setDynamicEligibility(criteriaRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility data:', error);
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchEligibilityData();
  }, []);

  // Fetch dynamic documents and settings
  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        setDocumentsLoading(true);
        const [documentsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/documents/nri_loan/active`),
          axios.get(`/api/admin/documents/settings/nri_loan`)
        ]);

        if (documentsRes.data.success) {
          setDynamicDocuments(documentsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents data:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocumentsData();
  }, []);

  // Fetch dynamic interest rates and settings
  useEffect(() => {
    const fetchInterestRatesData = async () => {
      try {
        setInterestRatesLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/interest-rates/nri_loan/active`),
          axios.get(`/api/admin/interest-rates/settings/nri_loan`)
        ]);

        if (cardsRes.data.success) {
          setDynamicInterestRates(cardsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestRatesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates data:', error);
      } finally {
        setInterestRatesLoading(false);
      }
    };

    fetchInterestRatesData();
  }, []);

  // Fetch dynamic FAQs and settings
  useEffect(() => {
    const fetchFAQsData = async () => {
      try {
        setFaqsLoading(true);
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/faqs/nri_loan/active`),
          axios.get(`/api/admin/faqs/settings/nri_loan`)
        ]);

        if (faqsRes.data.success) {
          setDynamicFAQs(faqsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs data:', error);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFAQsData();
  }, []);

  // Fetch dynamic CTA banner settings
  useEffect(() => {
    const fetchCTABannerData = async () => {
      try {
        setCtaBannerLoading(true);
        const response = await axios.get(`/api/admin/cta-banner/settings/nri_loan`);

        if (response.data.success && response.data.data) {
          setCtaBannerSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner data:', error);
      } finally {
        setCtaBannerLoading(false);
      }
    };

    fetchCTABannerData();
  }, []);


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
            
             // Schema for SEO
  const nriSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "serviceType": [
          "Home Loans",
          "Affordable Housing Finance"
        ],
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/nri-home-loan-india/#webpage",
        "url": "https://ahamhfc.com/nri-home-loan-india",
        "name": "NRI Home Loan in India - Aham Housing Finance",
        "description": "Explore NRI home loan options from Aham Housing Finance for eligible salaried Non-Resident Indians with attractive rates and flexible repayment tenure.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/nri-home-loan-india/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/nri-home-loan-india/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/NRI-Overview-1768371330857-610449939.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/nri-home-loan-india/#loan",
        "name": "NRI Home Loan",
        "alternateName": "NRI Home Loan in India",
        "url": "https://ahamhfc.com/nri-home-loan-india",
        "image": {
          "@id": "https://ahamhfc.com/nri-home-loan-india/#primaryimage"
        },
        "description": "Home loan for eligible Non-Resident Indians buying approved residential properties in India, with loan amounts from ₹10 lakhs to ₹50 lakhs and repayment tenure up to 15 years.",
        "category": "Housing Finance",
        "loanType": "NRI Home Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "offeredBy": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "amount": {
          "@type": "MonetaryAmount",
          "currency": "INR",
          "minValue": 1000000,
          "maxValue": 5000000
        },
        "loanTerm": {
          "@type": "QuantitativeValue",
          "maxValue": 15,
          "unitCode": "ANN"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Non-Resident Indian salaried borrowers"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/nri-home-loan-india/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "NRI Home Loan",
            "item": "https://ahamhfc.com/nri-home-loan-india"
          }
        ]
      }
    ]
  };




  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="NRI Home Loan in India: Attractive Rates | Aham Housing Finance"
        description="Apply for an NRI home loan in India with attractive interest rates. Check eligibility, required documents, and calculate EMIs online. Start your home journey today!"
        keywords="Aham NRI loan, Aham Housing Finance NRI loan, Aham NRI home loan, NRI home loan, home loan for NRIs, NRI salaried home loan, NRI housing loan India, loan for NRI property purchase, NRI property finance, home loan for non resident Indians, affordable NRI home loan, approved property NRI loan, NRI home loan up to 50 lakhs, NRI home loan tenure up to 15 years, Indian property loan for NRIs, Aham Housing Finance NRI home loan, NRI home purchase loan, overseas Indian home loan, Aham NRI property loan, housing finance for NRIs"
        canonical="https://ahamhfc.com/nri-home-loan-india"
        ogTitle="NRI Home Loan in India: Attractive Rates | Aham Housing Finance"
        ogDescription="Apply for an NRI home loan in India with attractive interest rates. Check eligibility, required documents, and calculate EMIs online. Start your home journey today!"
       schema={nriSchema}
           />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || 'img/loan-banners/NRI-Loan-Banner.webp'})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
              

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || "Own a Home in India with Aham NRI Home Loan"}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || "Specially designed for salaried NRIs—simple documentation, flexible tenure, and secure funding for your Indian property."}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href={banner?.primary_button_link || "https://aham.mbwhost.in/contact"} className="w-full sm:w-auto text-left">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                    >
                      {banner?.primary_button_text || "Apply Now"}
                    </motion.button>
                  </a>

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || "#"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                  {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">NRI Home Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
       <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || 'Home Loans for NRI Salaried'}
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {(overview?.description || fallbackOverview.description).split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {(overview?.notice_text || fallbackOverview.notice_text) && (
                  <div
                    onClick={handleRichTextClick}
                    className="mt-4 leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors cursor-pointer"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeAndFormatHtml(
                        overview?.notice_text || fallbackOverview.notice_text
                      )
                    }}
                  />
                )}

                {Array.isArray(overview?.features) && overview.features.length > 0 && (
                  <ul className="space-y-3 mt-6">
                    {overview.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || 'img/loan-overview/NRI-Overview.webp'}
                alt="Aham Housing Finance expert explaining NRI home loan eligibility and attractive interest rates to an NRI customer"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>







      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {benefitsSettings.section_title}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings.section_subtitle}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(dynamicBenefits.length > 0 ? dynamicBenefits : benefits).map((benefit, index) => {
              const IconComponent = benefit.icon_name ? (iconMap[benefit.icon_name] || BanknotesIcon) : benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                  {/* Icon */}
                  <div className="bg-gradient-to-br from-primary-600 to-primary-400 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <IconComponent className="h-9 w-9 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <div className="mt-16 flex justify-center">
            <a href="/contact" className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                Request a Quote
              </motion.button>
            </a>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings.section_title}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings.section_subtitle}
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(dynamicEligibility.length > 0 ? dynamicEligibility : eligibility).map((item, index) => {
              const criteriaText = typeof item === 'string' ? item : item.criteria;

              // Handle icon rendering
              let iconElement;
              if (item.icon_name && eligibilityIconMap[item.icon_name]) {
                const IconComp = eligibilityIconMap[item.icon_name];
                iconElement = <IconComp />;
              } else if (icons[index]?.icon) {
                iconElement = icons[index].icon;
              } else {
                iconElement = <User />;
              }

              const gradientFrom = item.gradient_from ? `from-${item.gradient_from}` : (icons[index]?.from || 'from-blue-500');
              const gradientTo = item.gradient_to ? `to-${item.gradient_to}` : (icons[index]?.to || 'to-cyan-400');

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                  group backdrop-blur-xl bg-white/70 
                  border border-white/40 shadow-xl rounded-2xl 
                  p-6 flex flex-col items-center text-center gap-5 
                  hover:-translate-y-2 hover:shadow-2xl 
                  transition-all duration-300
                "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                    w-14 h-14 rounded-xl flex items-center justify-center 
                    bg-gradient-to-br ${gradientFrom} ${gradientTo}
                    text-white shadow-lg group-hover:scale-105 transition
                  `}
                  >
                    {iconElement}
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {criteriaText}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(() => {
                  // Get documents for active category
                  const categoryDocs = dynamicDocuments.length > 0
                    ? dynamicDocuments.filter(doc => doc.category === activeTab)
                    : (documents[activeTab] || []).map(doc => ({ document_name: doc }));

                  return categoryDocs.map((doc, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                      className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                    >
                      <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                        <FileText className="h-6 w-6" />
                      </div>
                      <span className="text-gray-900 font-semibold">{doc.document_name || doc}</span>
                    </motion.li>
                  ));
                })()}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestRatesSettings.section_title}
              <span className="block text-primary-200 mt-2">{interestRatesSettings.section_subtitle}</span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestRatesSettings.intro_text}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {dynamicInterestRates.length > 0 ? (
                  dynamicInterestRates.map((card, index) => (
                    <motion.div
                      key={card.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 + index * 0.2 }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">✨</div>
                      <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </motion.div>
                  ))
                ) : (
                  <>
                    {/* Fallback static cards */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">🏠</div>
                      <h4 className="text-xl font-semibold mb-2">Flexible Rates</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        Rates tailored based on loan amount, tenure, credit profile & location.
                      </p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7 }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">🔍</div>
                      <h4 className="text-xl font-semibold mb-2">100% Transparency</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        No hidden fees. No surprise charges. Complete clarity from start to finish.
                      </p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.9 }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">🤝</div>
                      <h4 className="text-xl font-semibold mb-2">Dedicated Support</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        Our expert team guides you at every stage of your construction journey.
                      </p>
                    </motion.div>
                  </>
                )}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestRatesSettings.closing_text}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestRatesSettings.cta_link}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestRatesSettings.cta_text}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqsSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {faqsSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(dynamicFAQs.length > 0 ? dynamicFAQs : faqs).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: activeFAQ === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown
                        className={`h-6 w-6 ${activeFAQ === index ? 'text-primary-600' : 'text-gray-400'
                          }`}
                      />
                    </motion.div>
                  </div>
                  <motion.div
                    initial={false}
                    animate={{
                      height: activeFAQ === index ? 'auto' : 0,
                      opacity: activeFAQ === index ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="text-gray-600 mt-4 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings.title}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings.description}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBannerSettings?.button_link || "https://aham.mbwhost.in/contact"}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings?.button_text || "Talk to Our Loan Expert"}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default NRILoan;